import React from 'react';
import OuiNonToggle from './oui-non-toggle.js';
import { getMissingMasseFields } from '../../utils/fermer-enregistrement-capture.js';
import {
  quantiteConsommeCompletee,
  quantiteRemisAEauCompletee,
  quantitesPeseCompletees,
  quantiteNonPeseCompletee,
} from '../../utils/fermer-enregistrement-peche-capture.js';

const clampQuantity = (rawValue) => {
  if (rawValue === '' || rawValue === null || rawValue === undefined) {
    return '';
  }
  const n = Number(rawValue);
  if (Number.isNaN(n)) {
    return '';
  }
  if (n < 0) {
    return '0';
  }
  if (n > 999) {
    return '999';
  }
  return String(Math.floor(n));
};

const formatDisplayValue = (value) => {
  if (value === '' || value === null || value === undefined) {
    return '';
  }
  if (Number(value) === 0) {
    return '';
  }
  return value;
};

const QuantityField = ({ label, value, onChange, hasError, errorMessage }) => (
  <div className={`fec-capture-field-row ${hasError ? 'fec-capture-field-row--error' : ''}`}>
    <span className="fec-capture-field-label">{label}</span>
    <input
      type="number"
      min="0"
      max="999"
      step="1"
      className={`fec-capture-input ${hasError ? 'fec-capture-input--error' : ''}`}
      value={formatDisplayValue(value)}
      onChange={e => onChange(clampQuantity(e.target.value))}
      onWheel={(e) => e.currentTarget.blur()}
    />
    {hasError && errorMessage ?
      <span className="fec-field-error">{errorMessage}</span>
      : null
    }
  </div>
);

const MasseField = ({ label, value, quantiteValue, onChange, hasError, errorMessage }) => {
  const disabled = !(Number(quantiteValue) > 0);
  return (
    <div className={`fec-capture-field-row ${hasError ? 'fec-capture-field-row--error' : ''} ${disabled ? 'fec-capture-field-row--disabled' : ''}`}>
      <span className="fec-capture-field-label">{label}</span>
      <input
        type="number"
        min="0"
        step="0.001"
        disabled={disabled}
        className={`fec-capture-input ${hasError ? 'fec-capture-input--error' : ''}`}
        value={formatDisplayValue(value)}
        onChange={e => onChange(e.target.value)}
        onWheel={(e) => e.currentTarget.blur()}
      />
      {hasError && errorMessage ?
        <span className="fec-field-error">{errorMessage}</span>
        : null
      }
    </div>
  );
};

const QuestionBlock = ({ toggle, fields = null }) => (
  <div className="fec-capture-question-block">
    {toggle}
    {fields ?
      <div className="fec-capture-question-fields">
        {fields}
      </div>
      : null
    }
  </div>
);

const isToggleAnswered = (value) => value !== null && value !== undefined;

const PecheCaptureQuestionsForm = ({ texts, detail, onChange }) => {
  const missingMasseFields = getMissingMasseFields(detail);
  const showRemisAEau = isToggleAnswered(detail.aConsommeEspece)
    && quantiteConsommeCompletee(detail);
  const showPese = showRemisAEau && isToggleAnswered(detail.aRemisAEau)
    && quantiteRemisAEauCompletee(detail);
  const showNonPese = showPese && isToggleAnswered(detail.aPese)
    && quantitesPeseCompletees(detail);
  const showDivers = showNonPese && isToggleAnswered(detail.aNonPese)
    && quantiteNonPeseCompletee(detail);

  const handleQuantiteChange = (quantiteField, masseField, rawValue) => {
    const clamped = clampQuantity(rawValue);
    const extraUpdates = !Number(clamped) ? { [masseField]: '' } : null;
    onChange(quantiteField, clamped, extraUpdates);
  };

  const renderPeseGroup = (title, quantiteField, masseField) => {
    const masseMissing = missingMasseFields.includes(masseField);
    return (
      <div key={quantiteField} className="fec-capture-subgroup">
        <div className="fec-capture-subgroup-title">{title}</div>
        <QuantityField
          label={texts.labelQuantite}
          value={detail[quantiteField]}
          onChange={value => handleQuantiteChange(quantiteField, masseField, value)}
        />
        <MasseField
          label={texts.labelMasse}
          value={detail[masseField]}
          quantiteValue={detail[quantiteField]}
          hasError={masseMissing}
          errorMessage={masseMissing ? texts.errorMasseRequired : ''}
          onChange={value => onChange(masseField, value)}
        />
      </div>
    );
  };

  return (
    <div className="fec-capture-questions fec-capture-species-card">
      <div className="fec-capture-species-title">
        <span className="droit-acces-span">{detail.descriptionEspece}</span>
      </div>

      <div className="fec-capture-questions-body">
        <QuestionBlock
          toggle={
            <OuiNonToggle
              texts={texts}
              label={texts.questionConsommeEspece}
              value={detail.aConsommeEspece}
              onChange={value => onChange('aConsommeEspece', value)}
            />
          }
          fields={detail.aConsommeEspece === true ?
            <QuantityField
              label={texts.labelQuantite}
              value={detail.consommeQuantite}
              onChange={value => onChange('consommeQuantite', value)}
            />
            : null
          }
        />

        {showRemisAEau &&
          <QuestionBlock
            toggle={
              <OuiNonToggle
                texts={texts}
                label={texts.questionRemisAEau}
                value={detail.aRemisAEau}
                onChange={value => onChange('aRemisAEau', value)}
              />
            }
            fields={detail.aRemisAEau === true ?
              <QuantityField
                label={texts.labelQuantite}
                value={detail.remisAEau}
                onChange={value => onChange('remisAEau', value)}
              />
              : null
            }
          />
        }

        {showPese &&
          <QuestionBlock
            toggle={
              <OuiNonToggle
                texts={texts}
                label={texts.questionPese}
                value={detail.aPese}
                onChange={value => onChange('aPese', value)}
              />
            }
            fields={detail.aPese === true ?
              <>
                {renderPeseGroup(texts.groupEviscere, 'eviscereQuantite', 'eviscereMasse')}
                {renderPeseGroup(texts.groupEtete, 'eteteQuantite', 'eteteMasse')}
                {renderPeseGroup(texts.groupComplet, 'completQuantite', 'completMasse')}
              </>
              : null
            }
          />
        }

        {showNonPese &&
          <QuestionBlock
            toggle={
              <OuiNonToggle
                texts={texts}
                label={texts.questionNonPese}
                value={detail.aNonPese}
                onChange={value => onChange('aNonPese', value)}
              />
            }
            fields={detail.aNonPese === true ?
              <QuantityField
                label={texts.labelQuantite}
                value={detail.nonPeseeQuantite}
                onChange={value => onChange('nonPeseeQuantite', value)}
              />
              : null
            }
          />
        }

        {showDivers &&
          <div className="fec-capture-question-block fec-capture-question-block--divers">
          <div className="fec-capture-group-title">{texts.groupDivers}</div>
          <div className="fec-capture-question-fields fec-capture-question-fields--inline">
            <div className="fec-capture-field-row">
              <span className="fec-capture-field-label">{texts.labelLongueur}</span>
              <input
                type="number"
                min="0"
                step="0.1"
                className="fec-capture-input"
                value={formatDisplayValue(detail.longueur)}
                onChange={e => onChange('longueur', e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
              />
            </div>
            <div className="fec-capture-field-row">
              <span className="fec-capture-field-label">{texts.labelRemarque}</span>
              <input
                type="text"
                className="fec-capture-input fec-capture-input-text"
                value={detail.remarques || ''}
                onChange={e => onChange('remarques', e.target.value)}
              />
            </div>
          </div>
        </div>
        }
      </div>
    </div>
  );
};

export default PecheCaptureQuestionsForm;
